import { useEffect, useState } from "react";
import {
  Search,
  Stethoscope,
  Pill,
  FlaskConical,
  RefreshCw,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,

  TrendingUp,
  Building2,
} from "lucide-react";
import {
  AnalyticsService,
  SearchAnalyticsItem,
  SearchAnalyticsDashboardData,
} from "../../services/api/analytics.service";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import toast from "react-hot-toast";

const COLORS = {
  PRODUCT: "#3B82F6", // Blue
  LABTEST: "#8B5CF6", // Purple
  DOCTOR: "#10B981", // Emerald
};

export function SearchAnalyticsView() {
  const [loadingDashboard, setLoadingDashboard] = useState<boolean>(true);
  const [loadingListing, setLoadingListing] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<SearchAnalyticsDashboardData | null>(null);
  const [searchItems, setSearchItems] = useState<SearchAnalyticsItem[]>([]);

  // Pagination & Filtering
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>("ALL");
  const [searchFilter, setSearchFilter] = useState<string>("");

  const fetchDashboard = async () => {
    setLoadingDashboard(true);
    try {
      const res = await AnalyticsService.getSearchAnalyticsDashboard();
      if (res.success && res.data) {
        setDashboardData(res.data);
      } else {
        toast.error(res.message || "Failed to load dashboard analytics");
      }
    } catch (err) {
      toast.error("Failed to load dashboard analytics");
    } finally {
      setLoadingDashboard(false);
    }
  };

  const fetchListing = async (page: number) => {
    setLoadingListing(true);
    try {
      const res = await AnalyticsService.getSearchAnalytics(page, pageSize);
      if (res.success && res.data) {
        setSearchItems(Array.isArray(res.data) ? res.data : []);
        if (res.pagination) {
          setTotalPages(res.pagination.totalPages || 1);
          setTotalRecords(res.pagination.total || res.data.length);
        }
      } else {
        toast.error(res.message || "Failed to load search analytics listing");
      }
    } catch (err) {
      toast.error("Failed to load search analytics listing");
    } finally {
      setLoadingListing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    fetchListing(currentPage);
  }, [currentPage]);

  const handleRefresh = () => {
    fetchDashboard();
    fetchListing(currentPage);
  };

  // Filter local items by type or search text if needed
  const filteredItems = searchItems.filter((item) => {
    const matchesType =
      selectedTypeFilter === "ALL" ||
      item.selectedItemType?.toUpperCase() === selectedTypeFilter;

    const query = searchFilter.toLowerCase().trim();
    const matchesSearch =
      !query ||
      item.searchText?.toLowerCase().includes(query) ||
      item.patientName?.toLowerCase().includes(query) ||
      item.clinicName?.toLowerCase().includes(query) ||
      item.city?.toLowerCase().includes(query);

    return matchesType && matchesSearch;
  });

  // Prepare chart data
  const pieChartData = [
    {
      name: "Products",
      value: dashboardData?.productsSearchedCount || 0,
      color: COLORS.PRODUCT,
    },
    {
      name: "Lab Tests",
      value: dashboardData?.labtestsSearchedCount || 0,
      color: COLORS.LABTEST,
    },
    {
      name: "Doctors",
      value: dashboardData?.doctorsSearchedCount || 0,
      color: COLORS.DOCTOR,
    },
  ].filter((item) => item.value >= 0);

  const clinicChartData = (dashboardData?.clinicBreakdown || []).map((c) => ({
    name: c.clinicName || "Unknown Clinic",
    Total: c.totalSearches || 0,
    Doctors: c.doctorsCount || 0,
    Products: c.productsCount || 0,
    LabTests: c.labtestsCount || 0,
  }));

  const renderSelectedItemDetails = (item: SearchAnalyticsItem) => {
    const details = item.selectedItemDetails;
    if (!details) return <span className="text-gray-400 text-xs">-</span>;

    const itemType = item.selectedItemType?.toUpperCase();

    if (itemType === "PRODUCT") {
      const prodName = details.productName || details.name || "Product";
      const prodImg = details.productImage || details.main_image;
      return (
        <div className="flex items-center gap-2.5">
          {prodImg ? (
            <img
              src={prodImg}
              alt={prodName}
              className="w-9 h-9 object-cover rounded-lg border border-gray-200 shrink-0"
              onError={(e) => {
                (e.target as HTMLElement).style.display = "none";
              }}
            />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0">
              P
            </div>
          )}
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{prodName}</p>
            <p className="text-xs text-gray-500">
              {details.hsnNumber ? `HSN: ${details.hsnNumber}` : ""}
              {details.pack ? ` • Pack: ${details.pack}` : ""}
            </p>
          </div>
        </div>
      );
    }

    if (itemType === "LABTEST") {
      let testName = "Lab Test";
      if (Array.isArray(details) && details.length > 0) {
        testName = details.map((d) => d.name || d.testName).join(", ");
      } else if (typeof details === "object") {
        testName = details.name || details.testName || "Lab Test";
      }
      return (
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <FlaskConical className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{testName}</p>
            <p className="text-xs text-gray-500">ID: {item.selectedItemId}</p>
          </div>
        </div>
      );
    }

    if (itemType === "DOCTOR") {
      const docName = details.doctorName || details.name || "Doctor";
      const docPhone = details.mobileNumber || details.phone;
      return (
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <Stethoscope className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{docName}</p>
            {docPhone && <p className="text-xs text-gray-500">{docPhone}</p>}
          </div>
        </div>
      );
    }

    return (
      <div className="text-xs text-gray-700 truncate max-w-[180px]">
        {typeof details === "string" ? details : JSON.stringify(details)}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-emerald-600" />
            Search Analytics
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track user search trends, item conversions, and clinic-level demand analytics.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium text-sm rounded-xl shadow-xs transition-colors self-start sm:self-auto"
        >
          <RefreshCw className={`w-4 h-4 ${loadingDashboard || loadingListing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Analytics Sub-Tabs Navigation */}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Searches */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Total Searches
            </p>
            <h3 className="text-2xl font-bold text-gray-900 mt-1">
              {loadingDashboard ? "..." : dashboardData?.totalSearches ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Overall search queries</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <Search className="w-6 h-6" />
          </div>
        </div>

        {/* Card 2: Product Searches */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Products Searched
            </p>
            <h3 className="text-2xl font-bold text-blue-600 mt-1">
              {loadingDashboard ? "..." : dashboardData?.productsSearchedCount ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Medicines & Healthcare</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Pill className="w-6 h-6" />
          </div>
        </div>

        {/* Card 3: Labtest Searches */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Lab Tests Searched
            </p>
            <h3 className="text-2xl font-bold text-purple-600 mt-1">
              {loadingDashboard ? "..." : dashboardData?.labtestsSearchedCount ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Diagnostic packages</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <FlaskConical className="w-6 h-6" />
          </div>
        </div>

        {/* Card 4: Doctor Searches */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Doctors Searched
            </p>
            <h3 className="text-2xl font-bold text-emerald-600 mt-1">
              {loadingDashboard ? "..." : dashboardData?.doctorsSearchedCount ?? 0}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Specialists & Medical</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <Stethoscope className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Graphical Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pie Chart: Search Categories */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900">Searches by Category</h3>
            <p className="text-xs text-gray-500 mt-0.5">Distribution across item types</p>
          </div>

          <div className="w-full h-64 my-4 flex items-center justify-center">
            {loadingDashboard ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
            ) : pieChartData.every((d) => d.value === 0) ? (
              <p className="text-xs text-gray-400">No search category data available</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={85}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => [`${value} searches`, "Searches"]}
                  />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Bar Chart: Clinic Breakdown */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900">Clinic Demand Breakdown</h3>
            <p className="text-xs text-gray-500 mt-0.5">Search volume mapped by nearest clinic</p>
          </div>

          <div className="w-full h-64 my-4">
            {loadingDashboard ? (
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : clinicChartData.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <p className="text-xs text-gray-400">No clinic breakdown data available</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={clinicChartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                  <XAxis dataKey="name" stroke="#6B7280" fontSize={12} />
                  <YAxis stroke="#6B7280" fontSize={12} allowDecimals={false} />
                  <Tooltip />
                  <Legend verticalAlign="top" height={30} />
                  <Bar dataKey="Products" fill={COLORS.PRODUCT} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="LabTests" fill={COLORS.LABTEST} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Doctors" fill={COLORS.DOCTOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Listing Section */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
        {/* Table Controls & Filter Header */}
        <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-50/50">
          <div>
            <h3 className="text-base font-bold text-gray-900">Patient Search Activity</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Detailed logs of patient search queries and selected items
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search Input Filter */}
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Filter search text, patient..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 w-60 bg-white"
              />
            </div>

            {/* Type Filter Buttons */}
            <div className="inline-flex rounded-xl border border-gray-300 bg-white p-1">
              {["ALL", "PRODUCT", "LABTEST", "DOCTOR"].map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedTypeFilter(type)}
                  className={`px-3 py-1 text-xs font-semibold rounded-lg transition-colors capitalize ${selectedTypeFilter === type
                    ? "bg-emerald-600 text-white shadow-xs"
                    : "text-gray-600 hover:text-gray-900"
                    }`}
                >
                  {type === "ALL" ? "All" : type.toLowerCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-100/70 text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
              <tr>
                <th className="px-5 py-3.5">Patient</th>
                <th className="px-5 py-3.5">Search Text</th>
                <th className="px-5 py-3.5">Category</th>
                <th className="px-5 py-3.5">Selected Item</th>
                <th className="px-5 py-3.5">Clinic & Location</th>
                <th className="px-5 py-3.5">Date & Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loadingListing ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-500">
                    <div className="inline-flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600" />
                      Loading search analytics...
                    </div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-gray-500">
                    No search records found.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                    {/* Patient */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {item.patientImage ? (
                          <img
                            src={item.patientImage}
                            alt={item.patientName}
                            className="w-9 h-9 rounded-full object-cover border border-gray-200 shrink-0"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-800 font-bold flex items-center justify-center text-xs shrink-0">
                            {item.patientName?.charAt(0) || "P"}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">
                            {item.patientName || "Anonymous"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.patientPhoneNumber || item.patientId}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Search Text */}
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-800 border border-amber-200/60 rounded-lg text-xs font-semibold">
                        <Search className="w-3.5 h-3.5 text-amber-600" />
                        "{item.searchText}"
                      </span>
                    </td>

                    {/* Category Type */}
                    <td className="px-5 py-4">
                      {item.selectedItemType?.toUpperCase() === "PRODUCT" && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-semibold border border-blue-200">
                          <Pill className="w-3.5 h-3.5" /> Product
                        </span>
                      )}
                      {item.selectedItemType?.toUpperCase() === "LABTEST" && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-md text-xs font-semibold border border-purple-200">
                          <FlaskConical className="w-3.5 h-3.5" /> Lab Test
                        </span>
                      )}
                      {item.selectedItemType?.toUpperCase() === "DOCTOR" && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs font-semibold border border-emerald-200">
                          <Stethoscope className="w-3.5 h-3.5" /> Doctor
                        </span>
                      )}
                      {!["PRODUCT", "LABTEST", "DOCTOR"].includes(
                        item.selectedItemType?.toUpperCase() || ""
                      ) && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-semibold">
                            {item.selectedItemType || "N/A"}
                          </span>
                        )}
                    </td>

                    {/* Selected Item Details */}
                    <td className="px-5 py-4">{renderSelectedItemDetails(item)}</td>

                    {/* Clinic & Location */}
                    <td className="px-5 py-4">
                      <div>
                        <p className="font-medium text-gray-900 text-sm flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5 text-gray-400" />
                          {item.clinicName || "N/A"}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          {[item.city, item.state].filter(Boolean).join(", ") || "Unknown Location"}
                        </p>
                      </div>
                    </td>

                    {/* Date & Time */}
                    <td className="px-5 py-4 whitespace-nowrap text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        {new Date(item.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {new Date(item.createdAt).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-500">
            Showing Page <span className="font-semibold text-gray-800">{currentPage}</span> of{" "}
            <span className="font-semibold text-gray-800">{totalPages}</span> ({totalRecords} total searches)
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loadingListing}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages || loadingListing}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
